import { Injectable } from '@angular/core';
import { AccountService } from './account.service';
import { ANY_ADMIN } from './admin-levels';

export interface PagePermissions {
  [key: string]: {
    create?: string[];
    edit?: string[];
    read?: string[];
    delete?: string[];
    [action: string]: string[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class PagePermissionsService {
  private permissions: PagePermissions = {
    'TRANSFERS': {
      read: ['READ_TRANSFERS', 'EDIT_TRANSFERS', 'WRITE_TRANSFERS'],
      export: ['READ_TRANSFERS', 'EDIT_TRANSFERS', 'WRITE_TRANSFERS'],
      import: ['WRITE_TRANSFERS']
    },
    'DATASTORES': {
      create: ['WRITE_DATASTORE'],
      edit: ['EDIT_DATASTORE', 'WRITE_DATASTORE'],
      read: ['READ_DATASTORE', 'EDIT_DATASTORE', 'WRITE_DATASTORE'],
      delete: ['WRITE_DATASTORE'],
      import: ['SUPER_ADMIN'],
      export: ['SUPER_ADMIN']
    },
    'TABLES': {
      create: ['WRITE_TABLES'],
      edit: ['EDIT_TABLES', 'WRITE_TABLES'],
      read: ['READ_TABLES', 'EDIT_TABLES', 'WRITE_TABLES'],
      delete: ['WRITE_TABLES'],
      import: ['SUPER_ADMIN'],
      export: ['SUPER_ADMIN']
    },
    'DATAVIEWSETTINGS': {
      create: ['WRITE_DATAVIEWSETTINGS'],
      edit: ['EDIT_DATAVIEWSETTINGS', 'WRITE_DATAVIEWSETTINGS'],
      read: ['READ_DATAVIEWSETTINGS', 'EDIT_DATAVIEWSETTINGS', 'WRITE_DATAVIEWSETTINGS'],
      delete: ['WRITE_DATAVIEWSETTINGS'],
      import: ['WRITE_DATAVIEWSETTINGS'],
      export: ['WRITE_DATAVIEWSETTINGS']
    },
    'TAGS': {
      create: ['WRITE_TAGS'],
      edit: ['EDIT_TAGS', 'WRITE_TAGS'],
      read: ['READ_TAGS', 'EDIT_TAGS', 'WRITE_TAGS'],
      delete: ['WRITE_TAGS']
    },
    'PROVIDERS': {
      create: ['WRITE_PROVIDERS'],
      edit: ['EDIT_PROVIDERS', 'WRITE_PROVIDERS'],
      read: ['READ_PROVIDERS', 'EDIT_PROVIDERS', 'WRITE_PROVIDERS'],
      delete: ['WRITE_PROVIDERS']
    },
    'IMPORTS': {
      create: ['WRITE_IMPORTS'],
      edit: ['EDIT_IMPORTS', 'WRITE_IMPORTS'],
      read: ['READ_IMPORTS', 'EDIT_IMPORTS', 'WRITE_IMPORTS'],
      delete: ['WRITE_IMPORTS'],
      import: ['SUPER_ADMIN'],
      export: ['SUPER_ADMIN'],
      run: ['WRITE_IMPORTS'],
      copy: ['WRITE_IMPORTS'],
      newScript: ['SUPER_ADMIN'],
      scriptRun: ['SUPER_ADMIN'],
    },
    'EXPORTS': {
      create: ['WRITE_EXPORTS'],
      edit: ['EDIT_EXPORTS', 'WRITE_EXPORTS'],
      read: ['READ_EXPORTS', 'EDIT_EXPORTS', 'WRITE_EXPORTS'],
      delete: ['WRITE_EXPORTS'],
      import: ['SUPER_ADMIN'],
      export: ['SUPER_ADMIN'],
      run: ['WRITE_EXPORTS'],
      copy: ['WRITE_EXPORTS'],
      newScript: ['SUPER_ADMIN'],
      scriptRun: ['SUPER_ADMIN'],
    },
    'DATAVIEWS': {
      create: ['WRITE_DATAVIEWS'],
      edit: ['EDIT_DATAVIEWS', 'WRITE_DATAVIEWS'],
      read: ['READ_DATAVIEWS', 'EDIT_DATAVIEWS', 'WRITE_DATAVIEWS'],
      delete: ['WRITE_DATAVIEWS']
    },
    'CAMPAIGNS': {
      create: ['WRITE_CAMPAIGN'],
      edit: ['EDIT_CAMPAIGN', 'WRITE_CAMPAIGN'],
      read: ['READ_CAMPAIGN', 'EDIT_CAMPAIGN', 'WRITE_CAMPAIGN'],
      delete: ['WRITE_CAMPAIGN']
    },
    'SUBSCRIPTIONS': {
      create: ['WRITE_SUBSCRIPTIONS'],
      edit: ['EDIT_SUBSCRIPTIONS', 'WRITE_SUBSCRIPTIONS'],
      read: ['READ_SUBSCRIPTIONS', 'EDIT_SUBSCRIPTIONS', 'WRITE_SUBSCRIPTIONS'],
      delete: ['WRITE_SUBSCRIPTIONS']
    },
    'HTMLBLOCKS': {
      create: ['WRITE_HTMLBLOCK'],
      edit: ['EDIT_HTMLBLOCK', 'WRITE_HTMLBLOCK'],
      read: ['READ_HTMLBLOCK', 'EDIT_HTMLBLOCK', 'WRITE_HTMLBLOCK'],
      delete: ['WRITE_HTMLBLOCK']
    },
    'EMAILTRANSACTIONALS': {
      create: ['WRITE_EMAILTRANSACTIONAL'],
      edit: ['EDIT_EMAILTRANSACTIONAL', 'WRITE_EMAILTRANSACTIONAL'],
      read: ['READ_EMAILTRANSACTIONAL', 'EDIT_EMAILTRANSACTIONAL', 'WRITE_EMAILTRANSACTIONAL'],
      delete: ['WRITE_EMAILTRANSACTIONAL']
    },
    'HTML5TEMPLATES': {
      create: ['WRITE_HTML5TEMPLATE'],
      edit: ['EDIT_HTML5TEMPLATE', 'WRITE_HTML5TEMPLATE'],
      read: ['READ_HTML5TEMPLATE', 'EDIT_HTML5TEMPLATE', 'WRITE_HTML5TEMPLATE'],
      delete: ['WRITE_HTML5TEMPLATE']
    },
    'WORKFLOWS': {
      create: ['WRITE_WORKFLOW'],
      edit: ['EDIT_WORKFLOW', 'WRITE_WORKFLOW'],
      read: ['READ_WORKFLOW', 'EDIT_WORKFLOW', 'WRITE_WORKFLOW'],
      copy: ['WRITE_WORKFLOW'],
      run: ['WRITE_WORKFLOW'],
      test: ['WRITE_WORKFLOW'],
      delete: ['WRITE_WORKFLOW'],
      archive: ['WRITE_WORKFLOW']
    },
    'LANDINGPAGECONFIGURATIONS': {
      create: ['WRITE_LANDINGPAGECONFIGURATION'],
      edit: ['EDIT_LANDINGPAGECONFIGURATION', 'WRITE_LANDINGPAGECONFIGURATION'],
      read: ['READ_LANDINGPAGECONFIGURATION', 'EDIT_LANDINGPAGECONFIGURATION', 'WRITE_LANDINGPAGECONFIGURATION'],
      delete: ['WRITE_LANDINGPAGECONFIGURATION']
    },
    'INVOICECONTACTS': {
      create: ['WRITE_INVOICECONTACTS'],
      edit: ['EDIT_INVOICECONTACTS', 'WRITE_INVOICECONTACTS'],
      read: ['READ_INVOICECONTACTS', 'EDIT_INVOICECONTACTS', 'WRITE_INVOICECONTACTS'],
      delete: ['WRITE_INVOICECONTACTS']
    },
    'INVOICECOMPANIES': {
      create: ['WRITE_INVOICECOMPANIES'],
      edit: ['EDIT_INVOICECOMPANIES', 'WRITE_INVOICECOMPANIES'],
      read: ['READ_INVOICECOMPANIES', 'EDIT_INVOICECOMPANIES', 'WRITE_INVOICECOMPANIES'],
      delete: ['WRITE_INVOICECOMPANIES']
    },
    // Any admin manages the accounts of his scope: the gateway checks it on each route and only lists those accounts
    // (DMP2-845).
    'USERS': {
      create: [ANY_ADMIN],
      edit: [ANY_ADMIN],
      read: [ANY_ADMIN],
      delete: [ANY_ADMIN],
      sendEmail: [ANY_ADMIN],
      logOut: [ANY_ADMIN],
      manageAdminRights: ['SUPER_ADMIN']
    },
    'ROLES': {
      create: ['SUPER_ADMIN'],
      edit: ['SUPER_ADMIN'],
      read: ['SUPER_ADMIN'],
      delete: ['SUPER_ADMIN'],
    },
    'API': {
      read: ['SUPER_ADMIN'],
    }
  }

  constructor(private accountService: AccountService) { }

  getPermissionsFor(page: string, action: string): string[] {
    return this.permissions[page]?.[action] || [];
  }

  hasPagePermission(page: string, action: string, useExactPermissions = false): boolean {
    const requiredPerms = this.getPermissionsFor(page, action);
    if (requiredPerms.length === 0) {
      return true;
    }

    if (requiredPerms.includes('SUPER_ADMIN')) {
      return this.accountService.isSuperAdmin();
    }

    if (useExactPermissions) {
      return this.accountService.hasAnyPermission(requiredPerms);
    } else {
      return this.accountService.hasAnyMenu(requiredPerms);
    }
  }
}

